import {
  createDialogsSlice,
  DialogsSlice,
} from 'components/sections/admin-portal/machines/store/dialogs-slice';
import {
  createMachineDetailsSlice,
  MachineDetailsSlice,
} from 'components/sections/admin-portal/machines/store/machine-details-slice';
import {
  createMachinesSlice,
  MachinesSlice,
} from 'components/sections/admin-portal/machines/store/machines-slice';
import { createStore, useStore } from 'zustand';
import { devtools } from 'zustand/middleware';

const ENABLE_DEV_TOOLS = process.env.NODE_ENV !== 'production';

export type MachinesPortalStore = ReturnType<typeof createMachinesPortalStore>;
export type MachinesPortalState = MachinesSlice &
  DialogsSlice &
  MachineDetailsSlice;

const createMachinesPortalStore = () =>
  createStore<MachinesPortalState>()(
    devtools(
      (...args) => ({
        ...createMachinesSlice(...args),
        ...createDialogsSlice(...args),
        ...createMachineDetailsSlice(...args),
      }),
      { enabled: ENABLE_DEV_TOOLS }
    )
  );

const store = createMachinesPortalStore();

/**
 * TS overloads to allow for usage with or without a selector
 * e.g. const foo = useMachinesPortalStore()
 * e.g. const bar = useMachinesPortalStore(({ machineID }) => machineID)
 *
 * TS will infer the type `T` based on the selector function provided if you
 * decide to use useShallow to pick a specific part of the store.
 * e.g. const foo = useMachinesPortalStore(useShallow((state) => state.loading))
 */
export function useMachinesPortalStore(): MachinesPortalState;
export function useMachinesPortalStore<T>(
  selector: (state: MachinesPortalState) => T
): T;

export function useMachinesPortalStore<T>(
  selector?: (state: MachinesPortalState) => T
): T | MachinesPortalState {
  if (selector) {
    return useStore(store, selector);
  }

  return useStore(store);
}

// in case we need to access the store directly (e.g. useMachinesPortalStore.getState())
export function machinesPortalStore(): MachinesPortalStore {
  return store;
}
